import {
  type CreateParticipantNoteRequest,
  type Label,
  LabelType,
  type Template,
  TemplateStatus,
  TemplateType,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  dataAttr,
  MultiSelect,
  type MultiSelectOption,
  type OnChangeRequest,
  ScrollArea,
  Spin,
} from '@piccolohealth/ui';
import { P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaFile } from 'react-icons/fa';

import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal, showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useCreateParticipantNoteMutation } from '../../graphql/hooks/useCreateParticipantNoteMutation';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useTemplatesQuery } from '../../graphql/hooks/useTemplatesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantDocumentTemplatePreview } from '../templates/documents/ParticipantDocumentTemplatePreview';
import { TemplateStatusTag } from '../templates/TemplateStatusTag';
import { ParticipantNoteEditModal } from './ParticipantNoteEditModal';

interface TemplateLabelFilterControlProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const TemplateLabelFilterControl = (props: TemplateLabelFilterControlProps) => {
  const { value, onChange } = props;

  const { organization } = useAppContext();
  const { data, isLoading } = useLabelsQuery({
    organizationId: organization.id,
    type: LabelType.Template,
  });

  const options: MultiSelectOption<Label>[] = React.useMemo(() => {
    const labels = (data?.organization?.labels as Label[]) ?? [];
    return labels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label,
        color: label.color,
      };
    });
  }, [data?.organization?.labels]);

  const selectedValues: MultiSelectOption<Label>[] = React.useMemo(() => {
    return (value ?? []).flatMap((label) => {
      return options.find((o) => P.isEqual(o.raw.id, label)) ?? [];
    });
  }, [value, options]);

  const onChangePrime = React.useCallback(
    (req: OnChangeRequest<Label>) => {
      onChange(req.values.map(({ raw }) => raw.id));
    },
    [onChange],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChangePrime}
      size='sm'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

interface TemplateSidebarProps {
  templates: Template[];
  activeTemplateId: string | undefined;
  onClick: (template: Template) => void;
}

const TemplatesSidebar = (props: TemplateSidebarProps) => {
  const { templates, activeTemplateId, onClick } = props;

  const [name, setName] = React.useState('');
  const [labels, setLabels] = React.useState<string[]>([]);

  const filteredTemplates = React.useMemo(() => {
    return templates.filter((template) => {
      const nameMatches = template.name.toLowerCase().includes(name.trim().toLowerCase());
      const labelsMatch =
        P.isEmpty(labels) || template.labels.some((label) => labels.includes(label.id));
      return nameMatches && labelsMatch;
    });
  }, [labels, name, templates]);

  return (
    <ChakraV3.Stack h='full' w='280px' gap={4} flexShrink={0}>
      <ChakraV3.Stack>
        <ChakraV3.FormField label='Name'>
          <ChakraV3.Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Search templates...'
            size='sm'
          />
        </ChakraV3.FormField>

        <ChakraV3.FormField label='Labels'>
          <TemplateLabelFilterControl value={labels} onChange={setLabels} />
        </ChakraV3.FormField>
      </ChakraV3.Stack>

      <ChakraV3.Separator />

      <ScrollArea flexDir='column' overflowY='auto'>
        <ChakraV3.Stack pr={4}>
          <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
            Participant
          </ChakraV3.Text>
          {P.isEmpty(filteredTemplates) && (
            <ChakraV3.Text color='secondary' fontSize='sm'>
              No templates found...
            </ChakraV3.Text>
          )}
          {filteredTemplates.map((template) => (
            <ChakraV3.Button
              onClick={() => onClick(template)}
              key={template.id}
              {...{ 'data-active': dataAttr(activeTemplateId === template.id) }}
              variant='ghost'
              color='
              gray.600'
              size='sm'
              justifyContent='start'
              whiteSpace='initial'
              h='auto'
              px={3}
              py={2}
              rounded='md'
              fontSize='sm'
              fontWeight='semibold'
              textAlign='left'
            >
              <FaFile />
              {template.name}
            </ChakraV3.Button>
          ))}
        </ChakraV3.Stack>
      </ScrollArea>
    </ChakraV3.Stack>
  );
};

type FormValues = CreateParticipantNoteRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  id: z.string().optional(),
  participantId: z.string(),
  userId: z.string(),
  labelIds: z.array(z.string()),
  templateId: z.string().optional(),
});

interface ParticipantNoteCreateModalProps {
  participantId: string;
}

export const ParticipantNoteCreateModal = createModal<ParticipantNoteCreateModalProps>((props) => {
  const { participantId, modal } = props;
  const { errorToast, successToast, organization, user } = useAppContext();

  const { hide, remove, visible } = modal;

  const query = useTemplatesQuery({
    organizationId: organization.id,
    request: {
      type: [TemplateType.ParticipantNote],
      status: [TemplateStatus.Published],
    },
  });

  const mutation = useCreateParticipantNoteMutation();

  const methods = useForm<FormValues>({
    defaultValues: {
      participantId,
      userId: user.id,
      templateId: undefined,
      labelIds: [],
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const { handleSubmit, watch, setValue } = methods;

  const activeTemplateId = watch('templateId') ?? undefined;

  const templates = React.useMemo(() => {
    return (query.data?.organization?.templates.results as Template[]) ?? [];
  }, [query.data?.organization?.templates]);

  const activeTemplate = templates.find((template) => template.id === activeTemplateId);

  const selectTemplate = React.useCallback(
    (template: Template) => {
      setValue('templateId', template.id, { shouldDirty: true });
    },
    [setValue],
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: {
            id: values.id,
            participantId: values.participantId,
            templateId: values.templateId,
            userId: user.id,
            labelIds: values.labelIds,
          },
        })
        .then((res) => {
          hide();
          successToast('Note created successfully');
          showModal(ParticipantNoteEditModal, {
            participantId,
            participantNoteId: res.createParticipantNote.id,
          });
        })
        .catch((err) => {
          errorToast(`Error creating note: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, participantId, successToast, user.id],
  );

  React.useEffect(() => {
    const [template] = templates;
    if (!P.isUndefined(template)) {
      selectTemplate(template);
    }
  }, [templates, selectTemplate]);

  const content = P.run(() => {
    if (query.isLoading) {
      return <Spin />;
    }

    if (query.error) {
      return <PiccoloError error={query.error} />;
    }

    if (P.isEmpty(templates)) {
      return <ChakraV3.Text fontSize='sm'>No templates found</ChakraV3.Text>;
    }

    return (
      <ChakraV3.HStack w='full' h='full' gap={4} align='start'>
        <TemplatesSidebar
          templates={templates}
          activeTemplateId={activeTemplateId}
          onClick={selectTemplate}
        />
        <ChakraV3.Separator orientation='vertical' h='full' />
        <ChakraV3.Stack w='full' h='full' align='center'>
          <ChakraV3.Stack h='full' w='full' overflowY='auto' layerStyle='fade'>
            {activeTemplateId && (
              <ParticipantDocumentTemplatePreview
                templateId={activeTemplateId}
                participantId={participantId}
              />
            )}
          </ChakraV3.Stack>
          <ChakraV3.HStack
            w='full'
            bg='gray.50'
            layerStyle='outline.subtle'
            px={8}
            py={4}
            rounded='md'
            align='end'
          >
            {activeTemplate && (
              <ChakraV3.Stack w='full'>
                <ChakraV3.Text fontSize='lg' fontWeight='bold'>
                  {activeTemplate?.name}
                </ChakraV3.Text>
                <ChakraV3.HStack>
                  <TemplateStatusTag status={activeTemplate.status} />
                  {activeTemplate.labels.map((label) => (
                    <ChakraV3.Tag key={label.id} size='sm' colorPalette={label.color}>
                      {label.name}
                    </ChakraV3.Tag>
                  ))}
                </ChakraV3.HStack>
              </ChakraV3.Stack>
            )}
            <ChakraV3.Spacer />
            <ChakraV3.HStack>
              <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                Close
              </ChakraV3.Button>
              <HookedSubmitButton onClick={handleSubmit(onSubmit)}>Use template</HookedSubmitButton>
            </ChakraV3.HStack>
          </ChakraV3.HStack>
        </ChakraV3.Stack>
      </ChakraV3.HStack>
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a new note'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
